import { ProtobufClient } from 'api/protobuf/protobuf-client';
import { PackageProviderName } from 'data/ppoints/constants';
import { ALL_MAPPED_SHIPPING_RATE_DEFINITIONS_PATH } from 'features/settings/components/ppoints/payment-mapping/payment-mapping/payment-apping-market-driven/api/allMappedShippingRateDefinitionsApi';
import { ShippingType } from 'features/orders/models/ShippingType';
import { goBackendApi } from 'hooks/api/httpApi';
import { cache } from 'swr/_internal';
import { mapEveryBigintIntoNumber } from 'utils/map-every-bigint-into-number';
import { CommonMapper } from 'utils/mapper/common.mapper';
import { ShippingRateMappingRequestSchema } from '../../../../../pb/requests/v1/shipping_rate_mapping_request_pb';
import { ShippingRateMappingResponseSchema } from '../../../../../pb/responses/v1/shipping_rate_mappings_response_pb';
import {
    MappingShippingRate,
    MappingShippingRateTier,
    ShippingRateMappings,
    ShippingRateMappingsPayload,
} from '../models/shipping-rate-mapping';

export const shippingRateMappingPath = (provider: PackageProviderName): string =>
    `/api/v1/settings/mappings/shipping/rate/${provider}`;

export interface GetShippingRateMappingsOptions {
    /** Only return these shipping types. Sent as repeated `include` query params. */
    include?: ShippingType[];
    /** Omit these shipping types. Sent as repeated `exclude` query params. */
    exclude?: ShippingType[];
}

export const getShippingRateMappings = async (
    provider: PackageProviderName,
    { include, exclude }: GetShippingRateMappingsOptions = {}
): Promise<ShippingRateMappings | undefined> => {
    const response = await new ProtobufClient(goBackendApi())
        .getRequest(ShippingRateMappingResponseSchema, shippingRateMappingPath(provider), {
            ...(include?.length && { include }),
            ...(exclude?.length && { exclude }),
        })
        .catch((error) => {
            if (error.status === 404) {
                return undefined;
            }
            throw error;
        });

    return response ? (mapEveryBigintIntoNumber(response) as ShippingRateMappings) : undefined;
};

/**
 * A rate (or tier) saved earlier comes straight from the GET, and `mapEveryBigintIntoNumber` keeps the
 * response message's `$typeName` on it even though the model types omit it.
 */
type FromResponse<T> = T & { $typeName?: string };

/**
 * Replaces the provider's whole rate mapping set — the endpoint is a full PUT, so every country the
 * GET returned has to be sent back, not just the edited ones.
 *
 * `country_id`, each rate `id` and each tier's transit times are `uint64` in the proto, hence the
 * `CommonMapper.mapNumberIntoBigint` conversions.
 */
export const saveShippingRateMappings = async (
    provider: PackageProviderName,
    mappings: ShippingRateMappingsPayload
): Promise<ShippingRateMappings | undefined> => {
    const response = await new ProtobufClient(goBackendApi()).putRequest(
        ShippingRateMappingRequestSchema,
        ShippingRateMappingResponseSchema,
        shippingRateMappingPath(provider),
        {
            mappings: mappings.map((mapping) => ({
                countryId: CommonMapper.mapNumberIntoBigint(mapping.countryId),
                providerRates: mapping.providerRates.map((rates) => ({
                    shippingType: rates.shippingType,
                    // Spread as-is except the `uint64` fields. `$typeName` is dropped: a rate saved
                    // earlier still carries the GET response message's, which isn't this request's.
                    shippingRates: rates.shippingRates.map(
                        ({ $typeName, id, rates: tiers, ...rateFields }: FromResponse<MappingShippingRate>) => ({
                            ...rateFields,
                            id: CommonMapper.mapNumberIntoBigint(id),
                            rates: (tiers ?? []).map(
                                ({
                                    $typeName,
                                    minimumTransit,
                                    maximumTransit,
                                    ...tierFields
                                }: FromResponse<MappingShippingRateTier>) => ({
                                    ...tierFields,
                                    // The mapper returns `null` for an unset value; an optional proto field
                                    // is left unset with `undefined` instead.
                                    minimumTransit: CommonMapper.mapNumberIntoBigint(minimumTransit) ?? undefined,
                                    maximumTransit: CommonMapper.mapNumberIntoBigint(maximumTransit) ?? undefined,
                                })
                            ),
                        })
                    ),
                })),
            })),
        }
    );

    // The payment mapping's rate list is built from every provider's saved rate mappings, so its cached
    // copy is deleted from the (default, global) SWR cache — the list loads fresh on its next mount.
    cache.delete(ALL_MAPPED_SHIPPING_RATE_DEFINITIONS_PATH);

    // A 200 with an empty body reaches us as `null` (ProtobufClient returns null for a non-JSON
    // body), and `mapEveryBigintIntoNumber` does `Object.keys(value)` — so mapping it unguarded
    // turns a successful save into a TypeError. Same guard the GET above uses.
    return response ? (mapEveryBigintIntoNumber(response) as ShippingRateMappings) : undefined;
};
